import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Progress } from './ui/progress';
import {
  FileText,
  Clock,
  CheckCircle,
  Star,
  Award,
  ArrowRight,
  ArrowLeft,
  User,
  Calendar,
  Send
} from 'lucide-react';

const AvaliacaoPublica = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [avaliacao, setAvaliacao] = useState(null);
  const [perguntaAtual, setPerguntaAtual] = useState(0);
  const [respostas, setRespostas] = useState({});
  const [erro, setErro] = useState(null);
  const [finalizada, setFinalizada] = useState(false);
  const [resultado, setResultado] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadAvaliacao();
  }, [token]);

  const loadAvaliacao = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get(`${backendUrl}/api/avaliacao-publica/${token}`);
      setAvaliacao(response.data);
      
      // Inicializar respostas vazias
      const respostasIniciais = {};
      response.data.avaliacao.perguntas.forEach((pergunta, index) => {
        respostasIniciais[index] = null;
      });
      setRespostas(respostasIniciais);
      
    } catch (error) {
      console.error('Erro ao carregar avaliação:', error);
      setErro(error.response?.data?.detail || 'Erro ao carregar avaliação');
    } finally {
      setLoading(false);
    }
  };

  const handleResposta = (valor) => {
    setRespostas(prev => ({
      ...prev,
      [perguntaAtual]: valor
    }));
  };

  const proximaPergunta = () => {
    if (perguntaAtual < avaliacao.avaliacao.perguntas.length - 1) {
      setPerguntaAtual(perguntaAtual + 1);
    }
  };

  const perguntaAnterior = () => {
    if (perguntaAtual > 0) {
      setPerguntaAtual(perguntaAtual - 1);
    }
  };

  const finalizarAvaliacao = async () => {
    try {
      setSubmitting(true);
      
      // Converter respostas para o formato esperado pelo backend
      const respostasFormatadas = avaliacao.avaliacao.perguntas.map((pergunta, index) => ({
        pergunta_id: index,
        pergunta_texto: pergunta.texto || pergunta.pergunta || pergunta,
        resposta: respostas[index],
        pontuacao: respostas[index] || 0
      }));
      
      const response = await axios.post(`${backendUrl}/api/avaliacao-publica/${token}/responder`, {
        respostas: respostasFormatadas
      });
      
      setResultado(response.data.resultado);
      setFinalizada(true);
      
    } catch (error) {
      console.error('Erro ao finalizar avaliação:', error);
      setErro(error.response?.data?.detail || 'Erro ao enviar respostas');
    } finally {
      setSubmitting(false);
    }
  };

  const progresso = ((perguntaAtual + 1) / (avaliacao?.avaliacao.perguntas.length || 1)) * 100;
  const todasRespostasPreenchidas = avaliacao?.avaliacao.perguntas.every((_, index) => 
    respostas[index] !== null && respostas[index] !== undefined
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Carregando avaliação...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <FileText className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">Erro ao Carregar Avaliação</h3>
            <p className="text-red-600 mb-4">{erro}</p>
            <Button onClick={() => window.location.href = '/'} variant="outline">
              Voltar ao Início
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (finalizada && resultado) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-800">Avaliação Finalizada!</CardTitle>
            <CardDescription>Obrigado por responder à avaliação</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-green-800 font-medium">Score Final</p>
              <p className="text-2xl font-bold text-green-600">{resultado.score_final}%</p>
              <p className="text-sm text-green-600 mt-1">
                {resultado.total_perguntas} perguntas respondidas
              </p>
            </div>
            <p className="text-gray-600 text-sm">
              Suas respostas foram registradas com sucesso. O RH entrará em contato em breve.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!avaliacao) return null;

  const perguntaAtualData = avaliacao.avaliacao.perguntas[perguntaAtual];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-xl">{avaliacao.avaliacao.nome}</CardTitle>
                <CardDescription>Colaborador: {avaliacao.funcionario_nome}</CardDescription>
              </div>
            </div>
            
            {/* Progresso */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Pergunta {perguntaAtual + 1} de {avaliacao.avaliacao.perguntas.length}</span>
                <span>{Math.round(progresso)}%</span>
              </div>
              <Progress value={progresso} className="w-full" />
            </div>
          </CardHeader>
        </Card>

        {/* Pergunta Atual */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">
              {perguntaAtualData.texto || perguntaAtualData.pergunta || perguntaAtualData}
            </CardTitle>
            {perguntaAtualData.descricao && (
              <CardDescription>{perguntaAtualData.descricao}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                Escolha uma opção de 1 a 5 (1 = Discordo totalmente, 5 = Concordo totalmente):
              </p>
              
              <div className="grid grid-cols-5 gap-2">
                {[1, 2, 3, 4, 5].map((valor) => (
                  <Button
                    key={valor}
                    variant={respostas[perguntaAtual] === valor ? "default" : "outline"}
                    className="h-12 flex flex-col items-center justify-center"
                    onClick={() => handleResposta(valor)}
                  >
                    <span className="text-lg font-bold">{valor}</span>
                    <span className="text-xs">
                      {valor === 1 && 'Discordo'}
                      {valor === 2 && 'Discordo'}
                      {valor === 3 && 'Neutro'}
                      {valor === 4 && 'Concordo'}
                      {valor === 5 && 'Concordo'}
                    </span>
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Navegação */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={perguntaAnterior}
                disabled={perguntaAtual === 0}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Anterior
              </Button>

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  {todasRespostasPreenchidas ? 
                    "Todas as perguntas respondidas!" : 
                    `${Object.values(respostas).filter(r => r !== null && r !== undefined).length} de ${avaliacao.avaliacao.perguntas.length} respondidas`
                  }
                </p>
              </div>

              {perguntaAtual < avaliacao.avaliacao.perguntas.length - 1 ? (
                <Button
                  onClick={proximaPergunta}
                  disabled={respostas[perguntaAtual] === null || respostas[perguntaAtual] === undefined}
                >
                  Próxima
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={finalizarAvaliacao}
                  disabled={!todasRespostasPreenchidas || submitting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Finalizar Avaliação
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Descrição da Avaliação */}
        {avaliacao.avaliacao.descricao && (
          <Card className="mt-6">
            <CardContent className="pt-4">
              <Alert>
                <FileText className="h-4 w-4" />
                <AlertDescription>
                  {avaliacao.avaliacao.descricao}
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AvaliacaoPublica;